import type { ReactNode } from "react";
import { DumbbellIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ThemeToggle } from "@/components/app/theme-toggle";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface AuthShellProps {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/** Shared chrome for the three unauthenticated pages. */
export function AuthShell({ title, description, children, footer }: AuthShellProps) {
  const { t } = useTranslation();
  const platformName = import.meta.env.VITE_PLATFORM_NAME ?? t("app.name");

  return (
    <div className="bg-muted/30 flex min-h-svh flex-col">
      <div className="flex justify-end p-4">
        <ThemeToggle />
      </div>
      <div className="flex flex-1 items-center justify-center px-4 pb-16">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <span
              className="bg-primary text-primary-foreground flex size-12 items-center justify-center rounded-xl"
              aria-hidden="true"
            >
              <DumbbellIcon className="size-6" />
            </span>
            <span className="text-lg font-semibold">{platformName}</span>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{title}</CardTitle>
              {description ? <CardDescription>{description}</CardDescription> : null}
            </CardHeader>
            <CardContent>{children}</CardContent>
          </Card>

          {footer ? <div className="text-center text-sm">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
}
